import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import Loading from '$/components/loading/Loading';
import AhrefWithTarget from '$/components/ahrefWithTarget';
import { DataSiteService } from '$/services/DataSiteService';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import './index.scss';
import NoDataFound from '$/components/global/noData';
import InputBlock from '$/components/global/inputBlock';
import BixButton from '$/components/global/bixButton';

interface IDataSiteReconciliationGridProps {
  isMnpiUpload: boolean;
}

const DataSiteReconciliationGrid: React.FC<IDataSiteReconciliationGridProps> = prop => {
  const { isMnpiUpload } = prop;
  const [dataSiteSearchValue, setDataSiteSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countUniqueBixbyIds, setCountUniqueBixbyIds] = useState(0);
  const [reconciliationSummaryError, setReconciliationSummaryError] = useState(undefined as string | undefined);
  const [searchValueError, setSearchValueError] = useState(undefined as string | undefined);
  const [clientDataSiteReconciliationSummaries, setClientDataSiteReconciliationSummaries] = useState(
    undefined as Bixby.Models.ClientDataSiteReconciliationSummary[] | undefined
  );
  const [, setDataSiteGridAllApi] = useState(null as GridApi | null);
  const [, setDataSiteGridAllColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    setDataSiteSearchValue('');
    setClientDataSiteReconciliationSummaries(undefined);
    setCountUniqueBixbyIds(0);
  }, [isMnpiUpload]);

  const onGridReadyDataSiteAll = (params: any) => {
    setDataSiteGridAllApi(params.api);
    setDataSiteGridAllColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const onModelUpdated = (params: any) => {
    calculateUniqueCompanyBixbyIds(params);
  };

  const handleDataSiteSearchChange = event => {
    setDataSiteSearchValue(event.target.value);
  };

  const handleSearchDataSiteReconciliation = async () => {
    if (dataSiteSearchValue.length > 2) {
      setSearchValueError(undefined);
      setIsLoading(true);
      setReconciliationSummaryError(undefined);
      try {
        let recs: Bixby.Models.ClientDataSiteReconciliationSummary[];
        if (isMnpiUpload) {
          recs = await DataSiteService.getAllMnpiSummariesByDataSiteName(dataSiteSearchValue);
        } else {
          recs = await DataSiteService.getAllSummariesByDataSiteName(dataSiteSearchValue);
        }
        setClientDataSiteReconciliationSummaries(recs);
      } catch (e: any) {
        setReconciliationSummaryError('Error retrieving reconciliation summaries');
      } finally {
        setIsLoading(false);
      }
    } else {
      setSearchValueError('At least 3 character must be entered to search data site records');
    }
  };

  const calculateUniqueCompanyBixbyIds = (params: any) => {
    // get the number of unique CompanyBixbyIds in the reconciliation summaries
    const uniqueBixbyIds = new Set<string>();
    params.api.forEachNodeAfterFilter(node => {
      uniqueBixbyIds.add(node.data.companyBixbyId);
    });
    // N/A is not considered a unique CompanyBixbyId
    if (uniqueBixbyIds.has('N/A')) {
      uniqueBixbyIds.delete('N/A');
    }
    setCountUniqueBixbyIds(uniqueBixbyIds.size);
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <section className="data_site_reconciliation">
        {reconciliationSummaryError ? (
          <p className="error">{reconciliationSummaryError}</p>
        ) : (
          <div>
            <div className="data_site_reconciliation_list">
              <h3>Search Data Site Name</h3>
              <InputBlock title={`Search Data Site Names`}>
                <input name="datasitename" value={dataSiteSearchValue} onChange={handleDataSiteSearchChange} />
                {searchValueError ? <p className="error">{searchValueError}</p> : null}
              </InputBlock>
              <BixButton type="button" onClick={handleSearchDataSiteReconciliation}>
                Search
              </BixButton>
              <div className="data_site_grid_header">
                <h3>Client Data Sites - {isMnpiUpload ? 'MNPI' : 'Non-Mnpi'}</h3>
                <h3>Number of Unique BixbyIds: {countUniqueBixbyIds}</h3>
              </div>
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  {clientDataSiteReconciliationSummaries &&
                  !Bixby.Utils.isNullOrEmpty(clientDataSiteReconciliationSummaries) ? (
                    <AgGridReact
                      defaultColDef={{
                        flex: 1,
                        minWidth: 100,
                        sortable: true,
                        resizable: true,
                        lockPinned: true,
                      }}
                      suppressDragLeaveHidesColumns={true}
                      suppressMovableColumns={true}
                      suppressCellFocus={true}
                      animateRows={true}
                      rowHeight={32}
                      headerHeight={32}
                      rowData={clientDataSiteReconciliationSummaries}
                      onGridReady={onGridReadyDataSiteAll}
                      onModelUpdated={onModelUpdated}
                      getContextMenuItems={getContextMenuItems}
                      columnDefs={[
                        {
                          headerName: 'Data Site Name',
                          field: 'dataSiteName',
                          floatingFilter: true,
                          filter: 'agTextColumnFilter',
                          minWidth: 250,
                        },
                        {
                          headerName: 'Data Site Type Name',
                          field: 'dataSiteTypeName',
                          floatingFilter: true,
                          filter: 'agTextColumnFilter',
                          minWidth: 250,
                        },
                        {
                          headerName: 'Client Name',
                          field: 'clientName',
                          floatingFilter: true,
                          filter: 'agTextColumnFilter',
                          minWidth: 200,
                          cellRenderer: (params: ICellRendererParams) => {
                            return (
                              <AhrefWithTarget path={`admin/client/${params.data['clientId']}`} text={params.value} />
                            );
                          },
                        },
                        {
                          headerName: 'Bixby Access (Company)',
                          field: 'companyName',
                          floatingFilter: true,
                          filter: 'agTextColumnFilter',
                          minWidth: 200,
                          maxWidth: 300,
                          cellRenderer: (params: any) => {
                            if (params.data['companyId']) {
                              return (
                                <AhrefWithTarget
                                  path={`admin/company/${params.data['companyId']}`}
                                  text={params.data['companyName']}
                                />
                              );
                            } else {
                              return <div title={`${params.data['companyName']}`}>{params.data['companyName']}</div>;
                            }
                          },
                        },
                        {
                          headerName: 'Bixby Id',
                          field: 'companyBixbyId',
                          floatingFilter: true,
                          filter: 'agSetColumnFilter',
                          minWidth: 200,
                          maxWidth: 300,
                        },
                        {
                          headerName: 'Bixby Notes',
                          field: 'notes',
                          floatingFilter: true,
                          filter: 'agSetColumnFilter',
                          minWidth: 200,
                          maxWidth: 300,
                        },
                      ]}></AgGridReact>
                  ) : (
                    <NoDataFound dataType="reconciliations" />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }
};

const mapDispatchToProps = () => ({});

const mapStateToProps = () => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(DataSiteReconciliationGrid);
